import { Injectable } from '@angular/core';
import { authSelectors } from '@content-platform/web-angular-shared-util-auth';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store, select } from '@ngrx/store';
import { catchError, filter, map, switchMap, withLatestFrom } from 'rxjs';
import { roleActions, userRoleListActions, userPermissionListActions } from '../actions';
import { userRoleListSelectors, roleSelectors } from '../selectors';
import { RoleResource } from '../services';
import * as i0 from "@angular/core";
import * as i1 from "@ngrx/effects";
import * as i2 from "../services";
import * as i3 from "@ngrx/store";
/**
 * The role effects imported in {@link RoleApiModule }
 *
 */
export class RoleEffects {
    constructor(roleActions$, roleApi, store) {
        this.roleActions$ = roleActions$;
        this.roleApi = roleApi;
        this.store = store;
        /**
         * Loads all the availabe roles only when a user is logged in and triggers a LoadSuccess action.
         *
         * @returns { Observable<Action> } Observable with LoadSuccess action when all roles are retrieved
         */
        this.load$ = createEffect(() => this.roleActions$.pipe(ofType(roleActions.LOAD), switchMap(() => this.roleApi.query().pipe(map((apps) => new roleActions.LoadSuccess(apps)), catchError((error) => [
            new roleActions.LoadFailed({ error: error, message: 'Unable to load roles' })
        ])))));
        /**
         * Loads all the availabe roles only when a user is logged in and triggers a LoadSuccess action.
         *
         * @returns { Observable<Action> } Observable with LoadSuccess action when all roles are retrieved
         */
        this.loadById$ = createEffect(() => this.roleActions$.pipe(ofType(roleActions.LOAD_BY_ID), map((action) => action.payload), switchMap((payload) => this.roleApi.queryById(payload).pipe(map((apps) => new roleActions.LoadByIdSuccess(apps)), catchError((error) => [
            new roleActions.LoadByIdFailed({ error: error, message: 'Unable to load roles' })
        ])))));
        /**
         * Updates the role and triggers a UpdateSuccess action.
         *
         * @returns { Observable<Action> } Observable with UpdateSuccess action with the updated role
         */
        this.update$ = createEffect(() => this.roleActions$.pipe(ofType(roleActions.UPDATE), map((action) => action.payload), withLatestFrom(this.store.pipe(select(authSelectors.getUserId))), switchMap(([role, userId]) => {
            role.lastModifiedBy = userId;
            return this.roleApi.update(role).pipe(map((updatedRole) => new roleActions.UpdateSuccess(updatedRole)), catchError((error) => [
                new roleActions.LoadFailed({
                    error: error,
                    message: 'Unable to update role'
                })
            ]));
        })));
        /**
         * Forces Update of user Roles when a role is updated
      
         * @memberof RoleEffects
         */
        this.reloadUserProfiles$ = createEffect(() => this.roleActions$.pipe(ofType(roleActions.UPDATE_SUCCESS), map((action) => action.payload), withLatestFrom(this.store.pipe(select(userRoleListSelectors.getUserRoleListEntities))), switchMap(([role, userEntitiesInState]) => {
            const usersWithRole = Object.keys(userEntitiesInState).filter((id) => userEntitiesInState[id].roles.find((r) => r.id === role.id));
            const userRoleListUpdates = role.users
                .map((user) => (typeof user === 'string' ? user : user.objectId))
                .filter((id) => !!userEntitiesInState[id] && !usersWithRole.includes(id));
            return userRoleListUpdates
                .concat(usersWithRole)
                .map((userId) => new userRoleListActions.LoadById(userId));
        })));
        this.reloadUserPermissions$ = createEffect(() => this.roleActions$.pipe(ofType(roleActions.UPDATE_SUCCESS), map((action) => action.payload), withLatestFrom(this.store.pipe(select(userRoleListSelectors.getUserRoleListEntities))), switchMap(([role, userEntitiesInState]) => {
            const usersWithRole = Object.keys(userEntitiesInState).filter((id) => userEntitiesInState[id].roles.find((r) => r.id === role.id));
            const userRoleListUpdates = role.users
                .map((user) => (typeof user === 'string' ? user : user.objectId))
                .filter((id) => !!userEntitiesInState[id] && !usersWithRole.includes(id));
            return userRoleListUpdates
                .concat(usersWithRole)
                .map((userId) => new userPermissionListActions.LoadById(userId));
        })));
        /**
         * Create new role and CreateSuccess action.
         *
         * @returns { Observable<Action> } Observable with CreateSuccess action with the updated role
         */
        this.create$ = createEffect(() => this.roleActions$.pipe(ofType(roleActions.CREATE), map((action) => action.payload), withLatestFrom(this.store.pipe(select(authSelectors.getUserId))), withLatestFrom(this.store.pipe(select(authSelectors.getADUser))), withLatestFrom(this.store.pipe(select(roleSelectors.getAllRoleItems))), switchMap(([[[role, userId], user], roles]) => {
            role.createdBy = userId;
            role.lastModifiedBy = userId;
            return this.roleApi.create(role).pipe(switchMap((newRole) => {
                const actions = [new roleActions.CreateSuccess(newRole)];
                if (this.isUserPartOfUpdatedRole(user, roles, newRole.id, newRole)) {
                    actions.push(new userPermissionListActions.LoadById(user.profile.oid));
                    actions.push(new userRoleListActions.LoadById(userId));
                }
                return actions;
            }), catchError((error) => [
                new roleActions.CreateFailed({
                    error: error,
                    message: 'Unable to create role'
                })
            ]));
        })));
        /**
         * Delete role and trigger DeleteSuccess and trigger LoadById for user permissions list, if active user was part of the role.
         *
         * @returns { Observable<Action> } Observable with DeleteSuccess and UpdateSuccess action with deleted role id
         */
        this.delete$ = createEffect(() => this.roleActions$.pipe(ofType(roleActions.DELETE), map((action) => action.payload), withLatestFrom(this.store.pipe(select(authSelectors.getADUser))), withLatestFrom(this.store.pipe(select(roleSelectors.getAllRoleItems))), switchMap(([[roleId, user], roles]) => this.roleApi.delete(roleId).pipe(switchMap(() => {
            const actions = [new roleActions.DeleteSuccess(roleId)];
            if (this.isUserPartOfUpdatedRole(user, roles, roleId)) {
                actions.push(new userPermissionListActions.LoadById(user.profile.oid));
            }
            return actions;
        }), catchError((error) => [
            new roleActions.DeleteFailed({
                error: error,
                message: 'Unable to delete role'
            })
        ])))));
        this.deleteSuccess$ = createEffect(() => this.roleActions$.pipe(ofType(roleActions.DELETE_SUCCESS), map((action) => action.payload), withLatestFrom(this.store.pipe(select(userRoleListSelectors.getUserRoleListEntities))), switchMap(([roleId, userEntitiesInState]) => {
            const usersWithRole = Object.keys(userEntitiesInState).filter((id) => userEntitiesInState[id].roles.find((r) => r.id === roleId));
            return usersWithRole.map((userId) => new userRoleListActions.LoadById(userId));
        })));
        /**
         * Forces Update of user permissions when a role is updated
         *
         */
        this.reloadPermissionsAfterRoleUpdate$ = createEffect(() => this.roleActions$.pipe(ofType(roleActions.UPDATE_SUCCESS), map((action) => action.payload), withLatestFrom(this.store.pipe(select(authSelectors.getADUser))), withLatestFrom(this.store.pipe(select(roleSelectors.getAllRoleItems))), filter(([[role, user], roles]) => this.isUserPartOfUpdatedRole(user, roles, role.id, role)), map(([[, user]]) => new userPermissionListActions.LoadById(user.profile.oid))));
    }
    isUserPartOfUpdatedRole(user, roles, updatedRoleId, updatedRole) {
        const oldRole = roles.find((r) => r.id === updatedRoleId);
        if (oldRole && this.roleContainsUser(oldRole, user)) {
            return true;
        }
        if (updatedRole && this.roleContainsUser(updatedRole, user)) {
            return true;
        }
        return false;
    }
    roleContainsUser(role, user) {
        return !!role.users.find((u) => u.objectId === user.profile.oid);
    }
    static { this.ɵfac = function RoleEffects_Factory(__ngFactoryType__) { return new (__ngFactoryType__ || RoleEffects)(i0.ɵɵinject(i1.Actions), i0.ɵɵinject(i2.RoleResource), i0.ɵɵinject(i3.Store)); }; }
    static { this.ɵprov = /*@__PURE__*/ i0.ɵɵdefineInjectable({ token: RoleEffects, factory: RoleEffects.ɵfac }); }
}
//# sourceMappingURL=role.effects.js.map